/**
 * user-role の型 Role（02 5.3.2）と権限カタログ（API-USER-16）。
 */
import { z } from 'zod';
import { PERMISSION_CODES } from '../authz/permission-definitions';
import { BETA_FEATURE_TYPE_VALUES } from '../enums/beta-feature-type';
import { ROLE_CATEGORY_TYPE_VALUES } from '../enums/role-category-type';
import { zRequiredText, zVersion } from './common.schema';
import { CommonAttributesSchema, DeletedFilterSchema } from './list-query.schema';

const zRoleCode = z.string({ message: 'MSG-V-041' }).regex(/^[a-z][a-zA-Z0-9_]{1,49}$/, 'MSG-V-041');
const zPermissionCodes = z
  .array(z.enum(PERMISSION_CODES, { message: 'MSG-V-020' }))
  .max(130, 'MSG-V-020')
  .refine((codes) => new Set(codes).size === codes.length, 'MSG-V-020');
/** ベータ機能（274：旧と同じく選んで保存するだけ。どの機能の判定にも使わない） */
const zBetaFeatures = z
  .array(z.enum(BETA_FEATURE_TYPE_VALUES, { message: 'MSG-V-020' }))
  .max(BETA_FEATURE_TYPE_VALUES.length, 'MSG-V-020')
  .refine((values) => new Set(values).size === values.length, 'MSG-V-020');

export const RoleSchema = z
  .object({
    role_code: z.string(),
    role_name: z.string(),
    description: z.string(),
    display_order: z.number().int(),
    is_system_role: z.boolean(),
    role_category_type: z.enum(ROLE_CATEGORY_TYPE_VALUES),
    permission_codes: z.array(z.string()).optional(),
    /** 274：ベータ機能（既存のロールは空配列） */
    beta_features: z.array(z.string()).optional(),
    user_count: z.number().int().optional(),
    deleted_at: z.string().nullable().optional(),
    delete_batch_code: z.string().nullable().optional(),
  })
  .merge(CommonAttributesSchema);

export const RoleCreateSchema = z
  .object({
    role_code: zRoleCode,
    role_name: zRequiredText(100, 'MSG-V-042'),
    description: zRequiredText(500, 'MSG-V-019'),
    display_order: z.number().int().min(0).max(999).optional(),
    permission_codes: zPermissionCodes,
    beta_features: zBetaFeatures.optional(),
  })
  .strict();

export const RoleUpdateSchema = z
  .object({
    role_name: zRequiredText(100, 'MSG-V-042').optional(),
    description: zRequiredText(500, 'MSG-V-019').optional(),
    display_order: z.number().int().min(0).max(999).optional(),
    permission_codes: zPermissionCodes.optional(),
    beta_features: zBetaFeatures.optional(),
    version: zVersion,
  })
  .strict();

/** API-USER-09 のクエリ（deleted のみ。ページングしない） */
export const RoleListQuerySchema = z.object({ deleted: DeletedFilterSchema.default('exclude') });

export const PermissionCatalogItemSchema = z.object({
  permission_code: z.string(),
  resource_type: z.string(),
  action_type: z.string(),
  resource_name: z.string(),
  action_name: z.string(),
  is_admin_fixed: z.boolean(),
});

export type Role = z.infer<typeof RoleSchema>;
export type RoleCreateInput = z.infer<typeof RoleCreateSchema>;
export type RoleUpdateInput = z.infer<typeof RoleUpdateSchema>;
export type PermissionCatalogItem = z.infer<typeof PermissionCatalogItemSchema>;
